// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-87b — combine same-drug prescriptions under one enrollment. TanStack Query hooks (E-02).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { attachTherapyRx, detachTherapyRx, listEligibleTherapyRx, listTherapyRx, type AttachSource } from '../../api/carerxCombine'
import { ApiError } from '../../api/client'

export const combineKeys = {
  therapyRx: (therapyId: string) => ['carerx', 'therapy-rx', therapyId] as const,
  eligible: (therapyId: string) => ['carerx', 'therapy-rx', therapyId, 'eligible'] as const,
}

/** The patient's Rx this enrollment could take (drawer Add Rx); fetched only when the picker opens. */
export function useEligibleRx(therapyId: string, enabled: boolean) {
  return useQuery({ queryKey: combineKeys.eligible(therapyId), queryFn: () => listEligibleTherapyRx(therapyId), enabled })
}

/** The enrollment's prescriptions (anchor first, then attached) and its expiry. */
export function useTherapyRx(therapyId: string) {
  return useQuery({ queryKey: combineKeys.therapyRx(therapyId), queryFn: () => listTherapyRx(therapyId), staleTime: 30_000 })
}

/**
 * Stress test 2026-10-21: the server REFUSED the attach (409 closed / expired / already covered / combining
 * switched off, 422 not on the patient / not PA-approved). The offer the user clicked was stale, so whatever
 * showed it must be read again — otherwise the same dead "Attach" button stays on screen.
 */
export function isRefusedAttach(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 409 || e.status === 422)
}

/** Attach a new same-drug prescription to an enrollment; `onDone` reloads the caller's list. A refused attach
 *  reloads the Add Rx picker's list at once (the dialog stays open with the reason); the enrollment's own list is
 *  reloaded by {@link useRefreshTherapyRx} when that dialog closes. */
export function useAttachRx(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { therapyId: string; rxNumber: string; source: AttachSource; reason?: string }) =>
      attachTherapyRx(v.therapyId, v.rxNumber, v.source, v.reason),
    onSuccess: async (_r, v) => { await qc.invalidateQueries({ queryKey: combineKeys.therapyRx(v.therapyId) }); onDone() },
    onError: async (e, v) => {
      if (isRefusedAttach(e)) await qc.invalidateQueries({ queryKey: combineKeys.eligible(v.therapyId) })
    },
  })
}

/** Reload one enrollment's prescriptions and its Add Rx list (after a refused attach, once its dialog closes). */
export function useRefreshTherapyRx() {
  const qc = useQueryClient()
  return (therapyId: string) => qc.invalidateQueries({ queryKey: combineKeys.therapyRx(therapyId) })
}

export function useDetachRx(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { therapyId: string; rxNumber: string; reason: string }) =>
      detachTherapyRx(v.therapyId, v.rxNumber, v.reason),
    onSuccess: async (_r, v) => { await qc.invalidateQueries({ queryKey: combineKeys.therapyRx(v.therapyId) }); onDone() },
  })
}
